import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateLocation, Location, UpdateLocation } from '@salonomy/types';
import { api, unwrap } from '../lib/api.js';
import { qk } from '../lib/query.js';

/**
 * Locations (WS6). A salon has one or more physical sites; staff are assigned to a location.
 * Read-many + create + patch — `PATCH /locations/:id` landed in the WS6 API delta.
 */

export function useLocations() {
  return useQuery({
    queryKey: qk.locations,
    queryFn: async (): Promise<Location[]> => unwrap(await api.GET('/v1/locations')),
  });
}

export function useCreateLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateLocation): Promise<Location> =>
      unwrap(await api.POST('/v1/locations', { body: body as never })),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.locations }),
  });
}

export function useUpdateLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; body: UpdateLocation }): Promise<Location> =>
      unwrap(
        await api.PATCH('/v1/locations/{id}', {
          params: { path: { id: input.id } },
          body: input.body as never,
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.locations }),
  });
}
